import { useMemo, type JSX } from "react";
import { Link, useParams, useRouteContext } from "@tanstack/react-router";
import { Bell, Calendar, LivancePay, UserPlus, Wallet } from "@repo/icons";
import { graphql } from "@repo/graphql-types/gql";
import { type FragmentType, useFragment } from "@repo/graphql-types";
import { cn } from "@repo/lib";
import { Button } from "@/components/button";

export const PatientDetailLastAppointmentFragment = graphql(/* GraphQL */ `
  fragment PatientDetailLastAppointmentFragment on pacientes {
    lastAppointment: Agendamentos(
      where: {
        codFinalidadeAgendamento: { _in: [1, 3] }
        _not: {
          _and: [
            { codAgendamentoOrigem: { _is_null: false } }
            { cancelado: { _eq: true } }
          ]
        }
      }
      order_by: { codAgendamento: desc }
      limit: 1
    ) {
      codAgendamento
      reminders: lembretes(
        where: {
          ativo: { _eq: true }
          dataEnviado: { _is_null: true }
          dataParaEnvio: { _gte: $today }
        }
        order_by: { dataParaEnvio: asc }
      ) {
        codAgendamento
      }
    }
  }
`);

interface PatientDetailButtonsProps {
  data: FragmentType<typeof PatientDetailLastAppointmentFragment>;
}

export const PatientDetailButtons = ({
  data,
}: PatientDetailButtonsProps): JSX.Element => {
  const { lastAppointment } = useFragment(PatientDetailLastAppointmentFragment, data);

  const { patientId } = useParams({ from: "/patients/$patientId/" });

  const { flags } = useRouteContext({ strict: false });
  const showNewPatientLayout = useMemo(
    () => flags["exibe-nova-tela-de-paciente"],
    [flags],
  );
  const showMembersRecommendation = useMemo(
    () => flags["exibe-botoes-de-indicacao-de-membros-para-pacientes"],
    [flags],
  );

  const appointmentId = lastAppointment[0]?.codAgendamento;

  const showReminderButton = lastAppointment[0]?.reminders.some(
    (reminder) => reminder.codAgendamento === appointmentId,
  );

  return (
    <div className="flex flex-col gap-3">
      <ScheduleButton
        patientId={Number(patientId)}
        showNewPatientLayout={showNewPatientLayout}
      />

      {showMembersRecommendation && patientId && showNewPatientLayout ? (
        <ReferralButton
          patientId={Number(patientId)}
          showNewPatientLayout={showNewPatientLayout}
        />
      ) : null}

      {patientId ? (
        <CreatePaymentButton
          patientId={Number(patientId)}
          showNewPatientLayout={showNewPatientLayout}
        />
      ) : null}

      {!showReminderButton && appointmentId ? (
        <ReminderButton
          appointmentId={appointmentId}
          showNewPatientLayout={showNewPatientLayout}
        />
      ) : null}
    </div>
  );
};

interface PatientDetailButtonProps {
  patientId: number;
  showNewPatientLayout: boolean;
}

export const ScheduleButton = ({
  patientId,
  showNewPatientLayout,
}: PatientDetailButtonProps): JSX.Element => {
  const buttonColor = showNewPatientLayout ? "secondaryLight" : "secondary";
  const buttonText = showNewPatientLayout ? "Agendar" : "Novo agendamento";
  return (
    <Link to="/appointment/create" search={{ patientId }}>
      <Button
        color={buttonColor}
        fullWidth
        Icon={Calendar}
        className={cn({
          "text-secondary-400 hover:text-secondary-400 fill-secondary-400":
            showNewPatientLayout,
          "text-white hover:text-white fill-white font-medium": !showNewPatientLayout,
        })}
      >
        {buttonText}
      </Button>
    </Link>
  );
};

interface ReminderButtonProps {
  appointmentId: number;
  showNewPatientLayout: boolean;
}

export const ReminderButton = ({
  appointmentId,
  showNewPatientLayout,
}: ReminderButtonProps): JSX.Element | null => {
  const buttonColor = showNewPatientLayout ? "secondaryLight" : "secondary";

  return (
    <Link
      to="/waiting-room/follow-up-appointment-reminder/$appointmentId"
      params={{ appointmentId: String(appointmentId) }}
    >
      <Button
        color={buttonColor}
        fullWidth
        Icon={Bell}
        className={cn({
          "text-secondary-400 hover:text-secondary-400 fill-secondary-400":
            showNewPatientLayout,
          "text-white hover:text-white fill-white font-medium": !showNewPatientLayout,
        })}
      >
        Lembrete de Retorno
      </Button>
    </Link>
  );
};

export const ReferralButton = ({ patientId }: PatientDetailButtonProps): JSX.Element => (
  <Link to="/recommendation/" search={{ patientId }}>
    <Button
      color="secondaryLight"
      fullWidth
      Icon={UserPlus}
      className="text-secondary-400 hover:text-secondary-400 fill-secondary-400"
    >
      Indicar Profissional
    </Button>
  </Link>
);

export const CreatePaymentButton = ({
  patientId,
  showNewPatientLayout,
}: PatientDetailButtonProps): JSX.Element | null => {
  if (!patientId) return null;

  const buttonColor = showNewPatientLayout ? "secondaryLight" : "secondary";
  const Icon = showNewPatientLayout ? Wallet : LivancePay;

  return (
    <Link
      to="/settings/payments/create"
      preload={false}
      search={{ patientId, origin: "Patient Detail" }}
    >
      <Button
        color={buttonColor}
        fullWidth
        Icon={Icon}
        className={cn({
          "text-secondary-400 hover:text-secondary-400 fill-secondary-400":
            showNewPatientLayout,
          "text-white hover:text-white fill-white font-medium": !showNewPatientLayout,
        })}
      >
        Criar Pagamento
      </Button>
    </Link>
  );
};
